<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: PlaceEvents                                           |
| Section: views/overviews                                         |
| Purpose: Place-events route combining map context and timelines. |
+------------------------------------------------------------------+
-->
<template>

    <div class="map_placeholder"></div>

    <l-map class="map" v-if="!placesLoading" :zoom="zoom" :center="mapCenter" style="height: 100%;" ref="leafletMap"
        @ready="onMapReady">
        <l-tile-layer :url="tileLayerUrl" :attribution="tileLayerAttribution" />
        <l-marker v-for="(place, index) in places" :key="index" :lat-lng="[place.latitude, place.longitude]"
            :icon="customIcon">
            <l-popup>
                <p class="popup-text">{{ place.place }}</p>
                <a :href="localePath(`/plaatsen/${place.ID}`)">{{ $t('placeEvents.viewEvents') }}</a>
            </l-popup>
        </l-marker>
    </l-map>

    <LoadingSpinner v-if="eventsLoading" />

    <div v-else class="ResearchPage_wrapper wrapper">
        <div>
            <!-- Title Section -->
            <h1 class="text-center">{{ placeName }}</h1>
            <p class="subtitle text-center">{{ $t('placeEvents.subtitle') }}</p>
        </div>

        <!-- Events Data -->
        <div class="PersonCardGroup_wrapper person-card-group-wrapper--compact"
            v-for="(members, eventType) in groupedEvents" :key="eventType">
            <h3>{{ localizedEventTypeLabels[eventType] || eventType }}</h3>
            <div class="PersonCardGroup">
                <PersonCard v-for="person in members" :key="person.nodeID || person.personID" :photoSrc="person.photoSrc"
                    :name="person.firstname + ' ' + person.lastname"
                    :years="formatEventDescription(eventType, person.eventDate, person.eventYear)" :gender="person.sex"
                    :href="localePath(`/person/${person.personID}`)"
                    :personID="person.personID" :firstname="person.firstname" :lastname="person.lastname"
                    :living="person.living" :placeholder="person.placeholder"
                    :isSmall="true" :class="{ 'PersonCard_small_guest': isPrivatePerson(person) }" />
            </div>
        </div>
    </div>
</template>

<script>
import { useAuth } from '@/composables/useAuth';
import { useLocale } from '@/composables/useLocale';
import PersonCard from '@/atoms/PersonCard.vue';
import LoadingSpinner from '@/atoms/LoadingSpinner.vue';
import { LMap, LTileLayer, LMarker, LPopup } from "@vue-leaflet/vue-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "leaflet.fullscreen";
import "leaflet.fullscreen/Control.FullScreen.css";
import { fetchPlaces, fetchPlaceEvents } from '@/api/apiService';
import { getDefaultPhoto } from '@/utils/photoUtils';
import axios from 'axios';
import { applyBreadcrumbSeo, applyPlaceSeo } from '@/utils/seoUtils';
import { isPrivateLivingPerson } from '@/utils/personPrivacy';
import { extractEventYear } from '@/utils/eventDateUtils.mjs';

export default {
    name: 'PlaceEvents',
    components: {
        PersonCard,
        LoadingSpinner,
        LMap,
        LTileLayer,
        LMarker,
        LPopup,
    },
    setup() {
        const { authToken } = useAuth();
        const { localePath, locale } = useLocale();
        return { authToken, localePath, locale };
    },
    data() {
        return {
            placeName: '', // The name of the place
            events: [], // Events fetched from the API
            groupedEvents: {}, // Events grouped by type (birth, death, etc.)
            eventTypeLabels: {},
            // Moved to localizedEventTypeLabels computed
            places: [],
            mapCenter: [0.0, 0.0], // Default center for the map
            zoom: 2, // Default zoom level
            mapInstance: null,
            tileLayerUrl: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
            tileLayerAttribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            customIcon: L.icon({
                iconUrl: require("@/assets/icons/pin.svg"), // Adjust to your icon location
                iconSize: [32, 32],
                iconAnchor: [16, 32],
            }),
            placesLoading: true,
            eventsLoading: true,
            mapReady: false,
        };
    },
    async created() {
        const placeId = this.$route.params.id;

        // Fetch place events
        try {
            this.eventsLoading = true;
            const events = await fetchPlaceEvents(placeId);

            // Fetch and augment photo data for each event
            const augmentedEvents = await Promise.all(
                events.map(async (event) => {
                    if (event.placeholder || !event.personID) {
                        return { ...event, photoSrc: '' };
                    }
                    let photoPath = '';
                    try {
                        const photoResponse = await axios.get(
                            `${process.env.VUE_APP_BACKEND_API_URL}/person-default-photo/${event.personID}`,
                            {
                                headers: {
                                    'X-API-KEY': process.env.VUE_APP_API_KEY
                                }
                            }
                        );
                        photoPath = photoResponse.data
                            ? `${process.env.VUE_APP_PHOTO_API_URL}/${encodeURIComponent(photoResponse.data.path)}`
                            : '';
                    } catch (error) {
                        console.error(`Error fetching profile image for personID ${event.personID}:`, error);
                    }

                    // Determine gender for fallback photo
                    const gender = event.sex === 'M' ? 'M' : event.sex === 'F' ? 'F' : null;

                    // Augment the event with photoSrc and fallback logic
                    return {
                        ...event,
                        photoSrc: photoPath || getDefaultPhoto(gender),
                    };
                })
            );

            this.events = augmentedEvents;
            this.placeName = this.events[0]?.eventPlace || this.$t('placeEvents.unknownLocation');
            document.title = `${this.placeName} | Sejarah Thümann`; // Update document title here
            this.updateSeo();

            // Group events by type
            this.groupedEvents = this.events.reduce((groups, event) => {
                if (!groups[event.eventType]) groups[event.eventType] = [];
                groups[event.eventType].push(event);
                return groups;
            }, {});
        } catch (error) {
            console.error("Error fetching events:", error);
        } finally {
            this.eventsLoading = false;
        }

        // Fetch places
        try {
            this.placesLoading = true;
            const places = await fetchPlaces();
            this.places = places.filter(
                place =>
                    place.latitude &&
                    place.longitude &&
                    !isNaN(parseFloat(place.latitude)) &&
                    !isNaN(parseFloat(place.longitude))
            );

            // Find the highlighted place
            const highlightedPlace = this.places.find(place => place.ID === Number(placeId));
            if (highlightedPlace) {
                this.mapCenter = [highlightedPlace.latitude, highlightedPlace.longitude];
            } else if (this.places.length > 0) {
                this.mapCenter = [this.places[0].latitude, this.places[0].longitude];
            }
        } catch (error) {
            console.error("Failed to fetch places:", error);
        } finally {
            this.placesLoading = false;
        }
    },
    computed: {
        localizedEventTypeLabels() {
            return {
                birth: this.$t('placeEvents.eventTypes.birth'),
                death: this.$t('placeEvents.eventTypes.death'),
                baptism: this.$t('placeEvents.eventTypes.baptism'),
                burial: this.$t('placeEvents.eventTypes.burial'),
                BURI: this.$t('placeEvents.eventTypes.burial'),
                marriage: this.$t('placeEvents.eventTypes.marriage'),
                OCCU: this.$t('placeEvents.eventTypes.OCCU'),
                EDUC: this.$t('placeEvents.eventTypes.EDUC'),
                ADDR: this.$t('placeEvents.eventTypes.ADDR'),
                ALIA: this.$t('placeEvents.eventTypes.ALIA'),
                IMMI: this.$t('placeEvents.eventTypes.IMMI'),
                CHRA: this.$t('placeEvents.eventTypes.CHRA'),
                NATU: this.$t('placeEvents.eventTypes.NATU'),
            };
        },
        placesWithCoordinates() {
            return this.places.filter(
                place =>
                    place.latitude &&
                    place.longitude &&
                    !isNaN(place.latitude) &&
                    !isNaN(place.longitude)
            );
        },
    },
    watch: {
        mapCenter(newCenter) {
            if (this.mapInstance && newCenter) {
                this.mapInstance.setView(newCenter, 10); // Adjust zoom level if needed
            }
        }
    },
    methods: {
        isPrivatePerson(person) {
            return isPrivateLivingPerson(person, this.authToken);
        },
        updateSeo() {
            const description = `Ontdek belangrijke gebeurtenissen die plaatsvonden in ${this.placeName || "deze locatie"}.`;
            const image = "https://sejarah-thumann.online/img/sejarah_thumann1080x781.4fc54a6a.jpg";
            const placeId = this.$route.params.id;

            applyPlaceSeo({
                name: this.placeName || "Onbekende locatie",
                description,
                image,
                placeId,
                currentLocale: this.locale,
                eventCount: this.events.length
            }, {
                includeJsonLd: true,
                includeHreflangs: false,
                noindex: true
            });

            const homeUrl = this.locale === 'nl'
                ? 'https://sejarah-thumann.online/'
                : `https://sejarah-thumann.online/${this.locale}/`;
            const placesUrl = this.locale === 'nl'
                ? 'https://sejarah-thumann.online/plaatsen'
                : `https://sejarah-thumann.online/${this.locale}/plaatsen`;
            const placeUrl = this.locale === 'nl'
                ? `https://sejarah-thumann.online/plaatsen/${placeId}`
                : `https://sejarah-thumann.online/${this.locale}/plaatsen/${placeId}`;

            applyBreadcrumbSeo([
                { name: 'Home', url: homeUrl },
                { name: 'Plaatsen', url: placesUrl },
                { name: this.placeName || 'Onbekende locatie', url: placeUrl }
            ]);
        },

        formatEventDescription(eventType, eventDate, eventYear = null) {
            const year = extractEventYear(eventDate, eventYear);
            if (year === null) return this.$t('placeEvents.unknownDate');
            const normalizedEventType = eventType === 'BURI' ? 'burial' : eventType;
            const key = `placeEvents.eventDescriptions.${normalizedEventType}`;
            const fallback = this.$t('placeEvents.eventDescriptions.unknown', { year });
            const translated = this.$t(key, { year });
            return translated === key ? fallback : translated;
        },
        onMapReady(map) {
            this.mapInstance = map;

            // Add fullscreen control
            L.control.fullscreen({
                position: "bottomleft", // Position of the button
                title: {
                    false: "View Fullscreen", // Title when the map is not fullscreen
                    true: "Exit Fullscreen", // Title when the map is fullscreen
                },
                content: null, // Custom content inside the button (optional)
            }).addTo(map);

            if (this.placesWithCoordinates.length === 0) {
                this.mapInstance.setView(this.mapCenter, this.zoom);
            } else if (this.mapCenter) {
                this.mapInstance.setView(this.mapCenter, 10); // Adjust zoom level if needed
            } else {
                setTimeout(() => {
                    this.adjustMapBounds(this.placesWithCoordinates);
                }, 200);
            }

            this.$nextTick(() => {
                this.mapInstance.invalidateSize();
                this.mapReady = true;
            });
        },
        adjustMapBounds(places) {
            if (!places || places.length === 0) return;

            if (places.length === 1) {
                // If there's only one place, set the map view to the place's coordinates with a default zoom level
                this.mapInstance.setView([places[0].latitude, places[0].longitude], 10);
            } else {
                // Otherwise, fit the map to the bounds of all places
                const bounds = L.latLngBounds(places.map(place => [place.latitude, place.longitude]));
                this.mapInstance.fitBounds(bounds, { padding: [48, 48] });
            }
        },
    },
    mounted() {
        this.updateSeo();
    },
};
</script>

<style scoped>
.map {
    z-index: 1;
    position: absolute;
    top: 110px;
    left: 0;
}

/* .popup-text {
    font-weight: 700;
    margin-bottom: 8px;
} */

.leaflet-container {
    height: 480px !important;
    width: 100% !important;
}

.leaflet-popup {
    margin-bottom: 48px;
}

.map_placeholder {
    height: 480px !important;
    width: 100% !important;
    background: linear-gradient(90deg, #ccc 25%, #ddd 50%, #ccc 75%);
    background-size: 200% 100%;
    animation: shimmer 2s infinite;
}

.subtitle {
    margin-top: 16px;
    margin-bottom: 0;
}

/* .column-gap {
    display: flex;
    flex-direction: column;
    gap: 72px;
} */

.leaflet-container:fullscreen {
    height: 100% !important;
    width: 100% !important;
}

.leaflet-container:-webkit-full-screen {
    height: 100% !important;
    width: 100% !important;
}

.leaflet-container:-ms-fullscreen {
    height: 100% !important;
    width: 100% !important;
}

.leaflet-container:fullscreen a.leaflet-fullscreen-button {
    background-color: #fff;
}

.person-card-group-wrapper--compact {
    padding: 0 20px !important;
}

@media (max-width: 860px) {

    .leaflet-container,
    .map_placeholder {
        height: 36vh !important;
    }

    .map {
        top: 88px;
    }
}
</style>
